/**
 * Preserve the `c2b` URL parameter across the whole journey.
 *
 * The product finder already forwards `c2b` from the diagnostic to the result
 * page (see product-finder/block.js). This handles every other navigation:
 * if the current URL carries `c2b`, we append the same value to all internal
 * (same-origin) links that don't already define it.
 *
 * Links are mostly rendered client-side by the block engine, so we run once on
 * DOMContentLoaded and keep watching the DOM with a MutationObserver.
 */
const propagateC2b = () => {
  if (typeof window === "undefined") return;

  const currentC2b = new URLSearchParams(window.location.search).get("c2b");
  if (currentC2b === null) return;

  const processLinks = (links) => {
    links.forEach((link) => {
      if (link.dataset.c2bPropagated) return;

      const rawHref = link.getAttribute("href");
      // Skip anchors, mailto:, tel:, javascript: and other non-navigations.
      if (!rawHref || rawHref.startsWith("#")) return;

      let target;
      try {
        target = new URL(rawHref, window.location.href);
      } catch {
        return;
      }

      if (target.protocol !== "http:" && target.protocol !== "https:") return;
      // Internal links only — never touch retailer / Click2Buy links.
      if (target.origin !== window.location.origin) return;
      // Respect an explicit value already set on the link.
      if (target.searchParams.has("c2b")) {
        link.dataset.c2bPropagated = "true";
        return;
      }

      target.searchParams.set("c2b", currentC2b);
      link.setAttribute("href", target.toString());
      link.dataset.c2bPropagated = "true";
    });
  };

  // Only inspect newly-added subtrees rather than re-scanning the whole DOM on
  // every mutation — keeps the cost proportional to what actually changed.
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches("a[href]")) processLinks([node]);
        processLinks(node.querySelectorAll("a[href]"));
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
  processLinks(document.querySelectorAll("a[href]"));
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", propagateC2b);
} else {
  propagateC2b();
}
